import Splide, { Options } from '@splidejs/splide';

export function accessoriePackagesSlider(): void {
    const sliderPairs = document.querySelectorAll<HTMLElement>('.splide-accessories-packages');

    if (sliderPairs.length === 0) return;

    sliderPairs.forEach((pair, pairIndex) => {
        const imagesEl = pair.querySelector<HTMLElement>('.splide-images');

        if (!imagesEl) return;

        // Generate unique IDs for this slider instance
        const uniqueId = `slider-${pairIndex}`;
        const imagesTrackId = `images-track-${uniqueId}`;

        // Add IDs to the tracks
        const imagesTrack = imagesEl.querySelector('.splide__track');

        if (imagesTrack) imagesTrack.id = imagesTrackId;

        // Create Splide instance
        const imagesOptions: Options = {
            gap: 24,
            pagination: false,
            type: 'loop',
            perMove: 1,
            drag: true,
            arrows: false,
            fixedWidth: 472,
            breakpoints: {
                1024: {
                    perMove: 1,
                },
                1440: {
                    perMove: 1,
                }
            }
        };

        const images = new Splide(imagesEl, imagesOptions);

        // Custom arrow functionality with dynamic accessibility
        const setupCustomArrows = () => {
            const parentContainer = pair.parentElement;

            if (!parentContainer) return;

            const prevArrow = parentContainer.querySelector<HTMLElement>('.splide__arrow--prev');
            const nextArrow = parentContainer.querySelector<HTMLElement>('.splide__arrow--next');

            // Show arrows by default (unless there's only one slide)
            const totalSlides = images.length;
            const shouldShowArrows = totalSlides > 1;

            const customArrows = parentContainer.querySelectorAll<HTMLElement>('.splide__arrow--prev, .splide__arrow--next');
            customArrows.forEach(arrow => {
                arrow.style.display = shouldShowArrows ? 'flex' : 'none';
            });

            // Function to update accessibility attributes
            const updateAccessibilityAttributes = () => {
                const currentIndex = images.index + 1;

                // Get slider heading for context (if available)
                const headingEl = parentContainer.querySelector('.eyebrow');
                const sliderName = headingEl?.textContent?.trim() || 'Image slider';

                if (prevArrow) {
                    const prevText = `Previous slide in ${sliderName} (currently ${currentIndex} of ${totalSlides})`;

                    prevArrow.setAttribute('aria-label', prevText);
                    prevArrow.setAttribute('title', prevText);
                    prevArrow.setAttribute('aria-controls', imagesTrackId);
                }

                if (nextArrow) {
                    const nextText = `Next slide in ${sliderName} (currently ${currentIndex} of ${totalSlides})`;

                    nextArrow.setAttribute('aria-label', nextText);
                    nextArrow.setAttribute('title', nextText);
                    nextArrow.setAttribute('aria-controls', imagesTrackId);
                }
            };

            // Set initial accessibility attributes immediately (before mounting)
            if (totalSlides > 0) {
                const headingEl = parentContainer.querySelector('.eyebrow');
                const sliderName = headingEl?.textContent?.trim() || 'Image slider';

                if (prevArrow) {
                    const prevText = `Previous slide in ${sliderName} (currently 1 of ${totalSlides})`;
                    prevArrow.setAttribute('aria-label', prevText);
                    prevArrow.setAttribute('title', prevText);
                    prevArrow.setAttribute('aria-controls', imagesTrackId);
                }

                if (nextArrow) {
                    const nextText = `Next slide in ${sliderName} (currently 1 of ${totalSlides})`;
                    nextArrow.setAttribute('aria-label', nextText);
                    nextArrow.setAttribute('title', nextText);
                    nextArrow.setAttribute('aria-controls', imagesTrackId);
                }
            }

            // Set up click handlers
            if (prevArrow) {
                prevArrow.addEventListener('click', (e) => {
                    e.preventDefault();
                    images.go('<');
                });
            }

            if (nextArrow) {
                nextArrow.addEventListener('click', (e) => {
                    e.preventDefault();
                    images.go('>');
                });
            }

            // Update accessibility attributes on mount and move
            images.on('mounted move', () => {
                updateAccessibilityAttributes();

                // Since type is 'loop', arrows should never be disabled
                if (prevArrow) prevArrow.classList.remove('disabled');
                if (nextArrow) nextArrow.classList.remove('disabled');
            });
        };

        // Handle overflow for images slider - SIMPLIFIED
        images.on('overflow', (isOverflow: boolean) => {
            images.options = {
                arrows: false,
                pagination: isOverflow,
                drag: isOverflow,
            };
            // Note: We no longer hide/show arrows here since they should always be visible
        });

        // Handle progress bar (sibling element of the pair)
        const bar = pair.nextElementSibling?.querySelector<HTMLElement>('.pair-progress-bar');
        images.on('mounted move', () => {
            const end = images.Components.Controller.getEnd() + 1;
            const rate = Math.min((images.index + 1) / end, 1);
            if (bar) {
                bar.style.width = `${100 * rate}%`;
            }
        });

        // Ensure sizing runs after images finish loading (race with lazy loading).
        images.on('mounted', () => {
            const slideImages: HTMLImageElement[] = [];

            images.Components.Slides.forEach((Slide) => {
                const img = Slide.slide.querySelector<HTMLImageElement>('img');
                if (img) slideImages.push(img);
            });

            if (slideImages.length === 0) return;

            let remaining = slideImages.length;
            const handleImageReady = () => {
                remaining -= 1;
                if (remaining <= 0) {
                    images.refresh();
                }
            };

            slideImages.forEach((img) => {
                if (img.complete) {
                    handleImageReady();
                } else {
                    img.addEventListener('load', handleImageReady, { once: true });
                    img.addEventListener('error', handleImageReady, { once: true });
                }
            });
        });

        // Run another refresh on full page load as a safety net for late assets.
        window.addEventListener('load', () => {
            images.refresh();
        }, { once: true });

        // Mount slider
        images.mount();

        // Setup custom arrows after mounting
        setupCustomArrows();
    });
}